import { useCallback, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { toast } from '@/components/Toaster/constants'
import { isTRPCClientError } from '@/utils/trpc/utils'

export type SubmitToastMode = 'both' | 'error' | 'success' | 'none'
type SubmitCallback = () => Promise<string | void | boolean>
export type SubmitFunction = (submitCallback: SubmitCallback) => Promise<boolean>
type SubmitToastOptions = {
    toastMode?: SubmitToastMode
    errorDuration?: number
    successDuration?: number
}

type SubmitStatus = 'idle' | 'submitting' | 'submitted' | 'error'

export const useSubmit = ({ toastMode = 'both', errorDuration, successDuration }: SubmitToastOptions = {}) => {
    const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle')

    const { t } = useTranslation()

    const toastRef = useRef<string | null>(null)

    const submit = useCallback<SubmitFunction>(
        async (submitCallback: SubmitCallback) => {
            if (toastRef.current) {
                toast.remove(toastRef.current)
                toastRef.current = null
            }

            try {
                setSubmitStatus('submitting')

                const result = await submitCallback()
                if (result === false) {
                    return false
                }

                setSubmitStatus('submitted')

                if (toastMode === 'success' || toastMode === 'both') {
                    toastRef.current = toast.success(
                        result && typeof result === 'string' ? result : t('components.form.submitSuccess'),
                        { duration: successDuration }
                    )
                }

                return true
            } catch (e) {
                let message = t('components.form.submitError')

                if (isTRPCClientError(e)) {
                    message = e.message
                }

                setSubmitStatus('error')

                if (toastMode === 'error' || toastMode === 'both') {
                    toastRef.current = toast.error(message, { duration: errorDuration })
                }

                return false
            }
        },
        [errorDuration, successDuration, t, toastMode]
    )

    return { submit, submitStatus, isSubmitting: submitStatus === 'submitting' }
}
